'use client';

import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';
import { SignInTitle } from '@/app_components/SignIn/Components/SignInSharedComponents';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { Form, FormControl, FormField, FormItem } from '@/app_components/ui/form';
import { InputV2 } from '@/app_components/ui/InputV2';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import type { SignInStatus } from '@/app_lib/types/accounts/signin';
import { UNEXPECTED_ERROR_MESSAGE, USER_TYPE_TO_SIGNIN_STATUS } from '@/app_lib/types/accounts/signin';
import type { CheckEmailApiResponse } from '@/app_lib/types/accounts/signinApi';
import { getDeviceForTracking } from '@/app_lib/utils/deviceUtils';
import { emailSchema } from '@/app_lib/utils/zod.helpers';

interface SignInInitialEmailFormProps {
  setEmail: Dispatch<SetStateAction<string>>;
  setSignInStatus: Dispatch<SetStateAction<SignInStatus>>;
  setDisableSocialLogins: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

export const SignInInitialEmailForm = ({
  setEmail,
  setSignInStatus,
  setDisableSocialLogins,
  disabled,
}: SignInInitialEmailFormProps) => {
  const form = useForm({
    defaultValues: { email: '' },
    reValidateMode: 'onSubmit',
  });

  const {
    formState: { isSubmitting, isSubmitSuccessful, isValid, errors },
    setError,
  } = form;

  useEffect(() => {
    setDisableSocialLogins(isSubmitting || isSubmitSuccessful);
  }, [isSubmitting, isSubmitSuccessful, setDisableSocialLogins]);

  const handleSubmit = async ({ email }: { email: string }) => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError('email', {
        message: parsed.error.issues[0].message,
        type: 'manual',
      });
      return;
    }

    const validEmail = parsed.data;

    try {
      const res = await fetch('/api/signin/check-email', {
        body: JSON.stringify({ email: validEmail }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      const response: CheckEmailApiResponse = await res.json();
      if (response.success) {
        const status = USER_TYPE_TO_SIGNIN_STATUS[response.userType];
        setEmail(validEmail);
        setSignInStatus(status);
        return;
      }
      setError('email', {
        message: response.message || UNEXPECTED_ERROR_MESSAGE,
        type: 'manual',
      });
    } catch {
      setError('email', {
        message: UNEXPECTED_ERROR_MESSAGE,
        type: 'manual',
      });
    }
  };

  const clearEmailError = () => {
    if (errors.email) {
      form.clearErrors('email');
    }
  };

  useEffect(() => {
    trackEvent(
      {
        deviceType: getDeviceForTracking(),
        eventAction: 'account_signin_step_1',
      },
      'signInEventTracker',
    );
  }, []);

  return (
    <div className='flex flex-col items-center justify-center layout-gap-xs w-full'>
      <SignInTitle title='Sign in or create an account' />
      <Form {...form}>
        <form
          className='flex flex-col items-center justify-center layout-gap-xs w-full'
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormField
            control={form.control}
            name='email'
            render={({ field, fieldState }) => (
              <FormItem className='flex w-full flex-col space-y-0 gap-xs'>
                <FormControl>
                  <InputV2
                    autoComplete='email'
                    autoFocus
                    error={fieldState.error?.message}
                    id='email'
                    label='Email'
                    name='email'
                    onChange={(e) => {
                      field.onChange(e);
                      clearEmailError();
                    }}
                    placeholder='Enter your email'
                    required
                    type='email'
                    value={field.value}
                  />
                </FormControl>
              </FormItem>
            )}
            rules={{
              required: 'Email is required',
            }}
          />
          <ButtonV2
            as='button'
            className='w-full max-h-12'
            disabled={isSubmitting || !isValid || disabled || isSubmitSuccessful}
            size='lg'
            type='submit'
            variant='primary'
          >
            {isSubmitting || isSubmitSuccessful ? <SpinningLoader /> : 'Continue'}
          </ButtonV2>
        </form>
      </Form>
    </div>
  );
};
